import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Menu, ArrowLeft, Wrench, Loader2, CheckCircle2, AlertTriangle,
  Info, GitBranch, Trash2, Play, Sparkles, Package, KeyRound, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { NexusLogo } from '../components/brand/NexusLogo';
import { Sidebar } from '../components/shell/Sidebar';
import { CreditsButton } from '../components/credits/CreditsButton';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';
import { useT } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';

/** Workshop Detail — v1.1 (Feb 2026). */
export default function WorkshopDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const t = useT();
  const { user } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [envInput, setEnvInput] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: any = null;
    async function tick() {
      if (!sessionId) return;
      try {
        const s = await apiService.getWorkshopSession(sessionId);
        if (cancelled) return;
        setSession(s);
        const terminal = ['analyzing_done', 'preview_ok', 'ready_to_push', 'pushed', 'failed'].includes(s?.status);
        if (!terminal) timer = setTimeout(tick, 3000);
      } catch { /* ignore */ }
    }
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const status = session?.status || 'cloning';
  const health = session?.health || null;
  const isFailed = status === 'failed';
  const isAnalyzing = ['cloning', 'analyzing'].includes(status);
  const canRepair = health?.preview_ok === true;

  const repoShortName = useMemo(() => {
    const url = session?.repo_url || '';
    return url.split('/').pop()?.replace(/\.git$/, '') || 'Repositorio';
  }, [session?.repo_url]);

  const findings = health?.findings || [];
  const envRequired: string[] = health?.env_required || [];

  const submitEnv = useCallback(async () => {
    if (!sessionId || !envInput.trim()) return;
    const values: Record<string, string> = {};
    for (const line of envInput.split(/\r?\n/)) {
      const m = line.match(/^\s*([A-Z0-9_]+)\s*=\s*(.*)\s*$/i);
      if (m) values[m[1]] = m[2];
    }
    if (Object.keys(values).length === 0) { toast.error(t('workshop.envInvalid')); return; }
    try {
      await apiService.provideWorkshopEnv(sessionId, values);
      toast.success(t('workshop.envSaved', { count: Object.keys(values).length }));
      setEnvInput('');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('workshop.envError'));
    }
  }, [sessionId, envInput, t]);

  const startRepair = useCallback(async () => {
    if (!sessionId || repairing) return;
    setRepairing(true);
    try {
      await apiService.startWorkshopRepair(sessionId);
      toast.success(t('workshop.repairStarted'));
      const s = await apiService.getWorkshopSession(sessionId);
      setSession(s);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('workshop.repairError'));
    } finally { setRepairing(false); }
  }, [sessionId, repairing, t]);

  const deleteSession = useCallback(async () => {
    if (!sessionId || deleting) return;
    if (!window.confirm(t('workshop.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await apiService.deleteWorkshopSession(sessionId);
      toast.success(t('workshop.deleted'));
      nav('/home', { replace: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('workshop.deleteError'));
      setDeleting(false);
    }
  }, [sessionId, deleting, t, nav]);

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'N';

  return (
    <>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="min-h-[100dvh] w-full flex flex-col nx-circuit" data-testid="workshop-page">
        <header className="relative flex items-center justify-between px-5 sm:px-8 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-white/5 transition"
                    aria-label={t('home.menu')} data-testid="workshop-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <NexusLogo size={30} showText />
          </div>
          <div className="flex items-center gap-3">
            <CreditsButton testId="workshop-credits-btn" />
            <AdminAvatarMenu isAdmin={user?.role === 'admin'} initials={firstName[0] || 'N'} />
          </div>
        </header>

        <div className="mx-4 sm:mx-8 rounded-2xl px-4 py-3 flex items-center gap-3"
             style={{ background: 'hsl(var(--bg-elev) / 0.7)', border: '1px solid hsl(var(--border))', backdropFilter: 'blur(14px)' }}>
          <button onClick={() => nav('/home')} className="p-1.5 rounded-lg hover:bg-white/5 transition"
                  aria-label={t('common.back')} data-testid="workshop-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-xl grid place-items-center"
               style={{ background: 'hsl(268 100% 70% / 0.15)', border: '1px solid hsl(268 100% 70% / 0.4)', color: 'hsl(268 100% 78%)' }}>
            <Wrench className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-medium truncate" data-testid="workshop-title" style={{ color: 'hsl(var(--text))' }}>{repoShortName}</div>
            <div className="text-[11px] opacity-60 truncate">{session?.repo_url || t('workshop.loading')}</div>
          </div>
          <StatusPill status={status} t={t} />
          <button onClick={deleteSession} disabled={deleting}
                  className="p-1.5 rounded-lg hover:bg-white/5 transition opacity-60 hover:opacity-100 disabled:opacity-30"
                  aria-label={t('workshop.delete')} data-testid="workshop-delete-btn">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <section className="flex-1 mx-4 sm:mx-8 my-6 grid gap-4 lg:grid-cols-[1fr,340px] pb-8">
          <div className="space-y-4">
            {isAnalyzing && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                          className="rounded-3xl p-6"
                          style={{ background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
                                   border: '1px solid hsl(var(--accent) / 0.3)', backdropFilter: 'blur(16px)' }}
                          data-testid="workshop-phase-card">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'hsl(var(--accent))' }} />
                  <div className="text-sm">{status === 'cloning' ? t('workshop.phaseCloning') : t('workshop.phaseAnalyzing')}</div>
                </div>
              </motion.div>
            )}

            {isFailed && session?.error && (
              <div className="rounded-3xl p-6" data-testid="workshop-failure-card"
                   style={{ background: 'hsl(0 84% 62% / 0.06)', border: '1px solid hsl(0 84% 62% / 0.3)' }}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'hsl(0 84% 62%)' }} />
                  <div className="flex-1">
                    <div className="font-medium mb-1">{t('workshop.failureTitle')}</div>
                    <div className="text-sm opacity-80">{session.error}</div>
                    <button onClick={() => window.location.reload()}
                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                            style={{ background: 'hsl(0 84% 62% / 0.15)', border: '1px solid hsl(0 84% 62% / 0.4)', color: 'hsl(0 84% 62%)' }}>
                      <RefreshCw className="w-3.5 h-3.5" /> {t('workshop.retry')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {health && <HealthCard health={health} t={t} />}
            {findings.length > 0 && <FindingsList findings={findings} t={t} />}
          </div>

          <div className="space-y-4">
            <SidebarCard title={t('workshop.branchTitle')} icon={<GitBranch className="w-4 h-4" />}>
              <div className="text-xs opacity-70">{t('workshop.branchHint')}</div>
              <div className="mt-2 px-2.5 py-1.5 rounded-lg font-mono text-[11px] break-all"
                   style={{ background: 'hsl(var(--bg) / 0.6)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
                   data-testid="workshop-branch-name">
                {session?.branch || '—'}
              </div>
            </SidebarCard>

            {envRequired.length > 0 && (
              <SidebarCard title={t('workshop.envTitle')} icon={<KeyRound className="w-4 h-4" />}>
                <div className="text-xs opacity-70 mb-2">{t('workshop.envHint')}</div>
                <ul className="mb-2 space-y-1">
                  {envRequired.slice(0, 6).map((k) => (
                    <li key={k} className="font-mono text-[10px] opacity-80">· {k}</li>
                  ))}
                </ul>
                <textarea rows={4} value={envInput} onChange={(e) => setEnvInput(e.target.value)}
                          placeholder={`DATABASE_URL=…\nAPI_KEY=…`}
                          className="w-full rounded-lg px-3 py-2 text-xs font-mono outline-none resize-none"
                          style={{ background: 'hsl(var(--bg) / 0.5)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
                          data-testid="workshop-env-input" />
                <button onClick={submitEnv} disabled={!envInput.trim()}
                        data-testid="workshop-env-save-btn"
                        className="mt-2 w-full py-2 rounded-lg text-xs font-medium transition disabled:opacity-40"
                        style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.4)', color: 'hsl(var(--accent))' }}>
                  {t('workshop.envSave')}
                </button>
              </SidebarCard>
            )}

            <SidebarCard title={t('workshop.actionsTitle')} icon={<Sparkles className="w-4 h-4" />}>
              <button onClick={startRepair} disabled={!canRepair || repairing}
                      data-testid="workshop-repair-btn"
                      className="w-full py-2.5 rounded-xl text-sm font-medium transition disabled:opacity-40"
                      style={{ background: canRepair ? 'hsl(var(--accent))' : 'hsl(var(--bg) / 0.5)',
                               color: canRepair ? '#031014' : 'hsl(var(--text-muted))',
                               border: canRepair ? '1px solid hsl(var(--accent))' : '1px solid hsl(var(--border))',
                               boxShadow: canRepair ? '0 6px 22px hsl(var(--accent) / 0.35)' : 'none' }}>
                {repairing
                  ? <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> {t('workshop.repairInFlight')}</span>
                  : <span className="inline-flex items-center gap-2 justify-center"><Play className="w-4 h-4" /> {t('workshop.repairBtn')}</span>}
              </button>
              <div className="text-[11px] opacity-60 mt-2">
                {canRepair ? t('workshop.repairReady') : t('workshop.repairBlocked')}
              </div>
            </SidebarCard>
          </div>
        </section>
      </main>
    </>
  );
}

const StatusPill: React.FC<{ status: string; t: any }> = ({ status, t }) => {
  const map: Record<string, { label: string; color: string; icon?: React.ReactNode }> = {
    cloning: { label: t('workshop.statusCloning'), color: 'hsl(38 92% 60%)', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    analyzing: { label: t('workshop.statusAnalyzing'), color: 'hsl(210 100% 65%)', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    analyzing_done: { label: t('workshop.statusReady'), color: 'hsl(var(--accent))', icon: <CheckCircle2 className="w-3 h-3" /> },
    preview_ok: { label: t('workshop.statusPreviewOk'), color: 'hsl(155 65% 60%)', icon: <CheckCircle2 className="w-3 h-3" /> },
    repairing: { label: t('workshop.statusRepairing'), color: 'hsl(268 100% 78%)', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    ready_to_push: { label: t('workshop.statusReadyPush'), color: 'hsl(155 65% 60%)', icon: <CheckCircle2 className="w-3 h-3" /> },
    pushed: { label: t('workshop.statusPushed'), color: 'hsl(155 65% 60%)', icon: <CheckCircle2 className="w-3 h-3" /> },
    failed: { label: t('workshop.statusFailed'), color: 'hsl(0 84% 62%)', icon: <AlertTriangle className="w-3 h-3" /> },
  };
  const m = map[status] || { label: status, color: 'hsl(var(--text-muted))' };
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
          style={{ background: m.color.replace(')', ' / 0.15)'), border: `1px solid ${m.color.replace(')', ' / 0.4)')}`, color: m.color }}
          data-testid="workshop-status-pill">
      {m.icon}{m.label}
    </span>
  );
};

const HealthCard: React.FC<{ health: any; t: any }> = ({ health, t }) => (
  <div className="rounded-3xl p-6"
       style={{ background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))', border: '1px solid hsl(var(--border))', backdropFilter: 'blur(16px)' }}
       data-testid="workshop-health-card">
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-lg grid place-items-center"
           style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.3)', color: 'hsl(var(--accent))' }}>
        <Package className="w-4 h-4" />
      </div>
      <div className="text-[11px] uppercase tracking-widest opacity-70">{t('workshop.analysisTitle')}</div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <HealthStat label={t('workshop.framework')} value={health.framework || '—'} />
      <HealthStat label={t('workshop.runtime')} value={health.runtime_version || t('workshop.notDetected')} />
      <HealthStat label={t('workshop.entryPoint')} value={health.entry_point || t('workshop.notDetected')} mono />
      <HealthStat label={t('workshop.services')} value={(health.services_detected || []).join(', ') || t('workshop.none')} />
    </div>
    <div className="mt-4 text-[11px] opacity-70">
      {health.can_boot ? t('workshop.canBoot') : t('workshop.needsHelp')}
    </div>
  </div>
);

const HealthStat: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="rounded-2xl px-3 py-2.5" style={{ background: 'hsl(var(--bg) / 0.4)', border: '1px solid hsl(var(--border))' }}>
    <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
    <div className={`mt-1 text-sm ${mono ? 'font-mono text-[12px]' : ''}`} style={{ color: 'hsl(var(--text))' }}>{value}</div>
  </div>
);

const FindingsList: React.FC<{ findings: any[]; t: any }> = ({ findings, t }) => (
  <div className="rounded-3xl p-6" data-testid="workshop-findings"
       style={{ background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))', border: '1px solid hsl(var(--border))', backdropFilter: 'blur(16px)' }}>
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-lg grid place-items-center"
           style={{ background: 'hsl(38 92% 60% / 0.14)', border: '1px solid hsl(38 92% 60% / 0.3)', color: 'hsl(38 92% 60%)' }}>
        <Info className="w-4 h-4" />
      </div>
      <div className="text-[11px] uppercase tracking-widest opacity-70">
        {t('workshop.findingsTitle', { count: findings.length })}
      </div>
    </div>
    <ul className="space-y-2.5">
      {findings.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5" data-testid={`workshop-finding-${i}`}>
          <div className="mt-0.5 w-5 h-5 rounded-md grid place-items-center shrink-0"
               style={{
                 background: f.severity === 'critical' ? 'hsl(0 84% 62% / 0.15)' :
                             f.severity === 'warning' ? 'hsl(38 92% 60% / 0.15)' : 'hsl(210 100% 65% / 0.15)',
                 color: f.severity === 'critical' ? 'hsl(0 84% 62%)' :
                        f.severity === 'warning' ? 'hsl(38 92% 60%)' : 'hsl(210 100% 65%)',
               }}>
            <AlertTriangle className="w-3 h-3" />
          </div>
          <div className="min-w-0">
            <div className="text-sm">{f.message}</div>
            {f.file_hint && <div className="mt-0.5 text-[10px] font-mono opacity-60">{f.file_hint}</div>}
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const SidebarCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="rounded-2xl p-4"
       style={{ background: 'hsl(var(--bg-elev) / 0.7)', border: '1px solid hsl(var(--border))', backdropFilter: 'blur(14px)' }}>
    <div className="flex items-center gap-2 mb-3 opacity-70">
      {icon}
      <span className="text-[10px] uppercase tracking-widest">{title}</span>
    </div>
    {children}
  </div>
);
